import type { Schemas } from "@10twenty/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { api, session, unwrap } from "../api";
import { forgetWebPush } from "../notifications/webPush";
import { sessionFromTokens, type StoredSession } from "./session";

interface AuthValue {
  /** The stored device session; null when signed out. */
  session: StoredSession | null;
  signIn: (result: Schemas["AuthSession"]) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

const subscribe = (listener: () => void) => session.subscribe(listener);
const getSnapshot = () => session.current();

export function AuthProvider({ children }: { children: ReactNode }) {
  const current = useSyncExternalStore(subscribe, getSnapshot);
  const queryClient = useQueryClient();
  const userId = current?.userId ?? null;

  // Cached data belongs to one user: drop it on sign-out or account switch
  // (including those made in another tab).
  useEffect(() => () => queryClient.clear(), [queryClient, userId]);

  const signIn = useCallback(
    (result: Schemas["AuthSession"]) =>
      session.signIn(sessionFromTokens(result.user.id, result.device.id, result.tokens)),
    [],
  );

  const signOut = useCallback(async () => {
    await forgetWebPush();
    await session.signOut();
  }, []);

  const value = useMemo(() => ({ session: current, signIn, signOut }), [current, signIn, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside <AuthProvider>");
  return value;
}

/** The signed-in user. */
export function useMe() {
  const { session: current } = useAuth();
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => unwrap(await api.GET("/me")),
    enabled: current !== null,
  });
}
